import type { ToastServiceMethods } from 'primevue'
import type { FormFieldState } from '@primevue/forms/form'
import * as yup from 'yup'

export const getOpenFieldTypeEditFields = (
  fieldType?: components['schemas']['FieldTypeEnum']
) => {
  const _fields: FormField[] = []

  if (!fieldType) return _fields

  if (fieldType === 'dropdown') {
    _fields.push({
      as: 'ListInputText',
      name: 'options',
      label: 'Options',
      schema: yup
        .array()
        .of(yup.string().required())
        .min(2)
        .required()
        .label('Options'),
    })
  }

  if (fieldType === 'foreign-key') {
    _fields.push(
      {
        as: 'InputText',
        label: 'URL',
        name: 'url',
        schema: yup.string().required().label('URL'),
      },
      {
        as: 'InputText',
        label: 'Option label',
        name: 'optionLabel',
        schema: yup.string().required().label('Option label'),
      },
      {
        as: 'InputText',
        label: 'Search param',
        name: 'searchParam',
        schema: yup.string().required().label('Search param'),
      }
    )
  }

  return _fields
}

export const getOpenFieldEditMeta = (
  fieldType: components['schemas']['FieldTypeEnum'],
  values: Record<string, any>
) => {
  const meta: Record<string, unknown> = {}

  if (fieldType === 'dropdown') {
    meta.options = values.options
  }

  if (fieldType === 'foreign-key') {
    meta.url = values.url
    meta.optionLabel = values.optionLabel
    meta.searchParam = values.searchParam
  }

  return meta
}

export const getOpenFieldFormField = (
  field: components['schemas']['ContractField']
): FormField => {
  const defaultParams = {
    label: 'Value',
    name: 'raw_value',
  }

  if (field.field_type === 'text-single')
    return {
      ...defaultParams,
      as: 'InputText',
      schema: yup.string().nullable(),
    }
  if (field.field_type === 'text-multiple')
    return {
      ...defaultParams,
      as: 'Editor',
      editorStyle: 'height: 320px',
      schema: yup.string().nullable(),
    }
  if (field.field_type === 'email')
    return {
      ...defaultParams,
      as: 'InputText',
      schema: yup.string().email().nullable(),
    }
  if (field.field_type === 'phone-number')
    return {
      ...defaultParams,
      as: 'InputText',
      schema: yup.string().nullable(),
    }
  if (field.field_type === 'number')
    return {
      ...defaultParams,
      as: 'InputNumber',
      mode: 'decimal',
      maxFractionDigits: 2,
      min: 0,
      schema: yup.number().min(0).nullable(),
    }
  if (field.field_type === 'currency')
    return {
      ...defaultParams,
      as: 'InputNumber',
      mode: 'currency',
      currency: 'USD',
      maxFractionDigits: 2,
      min: 0,
      schema: yup.number().min(0).nullable(),
    }
  if (field.field_type === 'percentage')
    return {
      ...defaultParams,
      as: 'InputNumber',
      mode: 'decimal',
      maxFractionDigits: 2,
      min: 0,
      max: 100,
      schema: yup.number().nullable().min(0).max(100),
    }
  if (field.field_type === 'checkbox')
    return {
      ...defaultParams,
      as: 'Checkbox',
      binary: true,
      schema: yup.boolean().nullable(),
    }
  if (field.field_type === 'dropdown')
    return {
      ...defaultParams,
      as: 'Select',
      // @ts-expect-error "Options must be present"
      schema: yup.string().oneOf(field.meta.options).nullable(),
      ...(field.meta ?? {}),
    }
  if (field.field_type === 'foreign-key')
    return {
      ...defaultParams,
      as: 'RemoteSelect',
      schema: yup.object().nullable(),
      ...(field.meta ?? {}),
    }
  if (field.field_type === 'date-signed')
    return {
      ...defaultParams,
      as: 'InputText',
      schema: yup.date().nullable(),
      type: 'date',
    }
  if (field.field_type === 'date-picker')
    return {
      ...defaultParams,
      as: 'InputText',
      schema: yup.date().nullable(),
      type: 'date',
    }
  if (field.field_type === 'signer-name')
    return {
      ...defaultParams,
      as: 'InputText',
      schema: yup.string().nullable(),
    }

  return { ...defaultParams, as: 'InputText', schema: yup.string().nullable() }
}

export const getOpenFieldDeserializedValue = (
  field?: components['schemas']['ContractField']
) => {
  if (!field || !field.raw_value) return

  const value = field.raw_value

  if (!value) return value

  if (field.field_type === 'checkbox') {
    if (value === 'true') return true
    if (value === 'false') return false
  }

  if (field.field_type === 'currency') {
    return formatNumber(field.raw_value, '$0,0.00', {
      currency: 'USD',
    })
  }

  if (field.field_type === 'foreign-key') {
    try {
      return JSON.parse(field.raw_value)[
        // @ts-expect-error "Option label must be present"
        field.meta.optionLabel
      ]
    } catch {
      return field.raw_value
    }
  }

  if (field.field_type === 'number') {
    return formatNumber(field.raw_value, '0,0.00')
  }

  if (field.field_type === 'percentage') {
    return formatNumber(field.raw_value, '0,0.00')
  }

  if (field.field_type === 'phone-number') {
    return formatPhoneNumber(field.raw_value)
  }

  return field.raw_value
}

export const setOpenFieldInternalValue = (
  field: components['schemas']['ContractField'],
  value?: unknown
) => {
  if (value === undefined || value === null || value === '') return value

  if (field.field_type === 'checkbox') {
    if (value === true) return 'true'
    if (value === false) return 'false'
  }

  if (field.field_type === 'foreign-key') return JSON.stringify(value)

  if (field.field_type === 'date-signed') {
    // @ts-expect-error 'Should be string'
    return value.split('T')[0]
  }

  return value
}

export const objectToFormData = (
  obj: Record<string, any>,
  form?: FormData,
  namespace?: string
): FormData => {
  const formData = form || new FormData()

  for (let key in obj) {
    if (!obj.hasOwnProperty(key) || obj[key] === undefined || obj[key] === null)
      continue

    const formKey = namespace ? `${namespace}[${key}]` : key
    const value = obj[key]

    if (value instanceof Date) {
      formData.append(formKey, value.toISOString())
    } else if (value instanceof File || value instanceof Blob) {
      formData.append(formKey, value)
    } else if (Array.isArray(value)) {
      value.forEach((element, index) => {
        if (element instanceof File || element instanceof Blob) {
          formData.append(`${formKey}[${index}]`, element)
        } else {
          objectToFormData({ [index]: element }, formData, formKey)
        }
      })
    } else if (typeof value === 'object') {
      formData.append(formKey, JSON.stringify(value))
    } else {
      formData.append(formKey, value.toString())
    }
  }

  return formData
}

export const processFormErrors = (
  error: any,
  toast: ToastServiceMethods,
  states?: Record<string, FormFieldState>
) => {
  if (!error || !error.data) return

  states = states ?? {}

  const { $umami } = useNuxtApp()
  $umami.track('api-error', {
    states: states,
    errorData: error.data,
  })

  for (const [key, value] of Object.entries(error.data)) {
    if (states[key]) {
      states[key].errors = Array.isArray(value) ? value : [value]
      states[key].invalid = true
      states[key].valid = false
    } else if (key === 'non_field_errors') {
      if (Array.isArray(value)) {
        value.forEach((message) => {
          toast.add({
            life: 5000,
            severity: 'error',
            summary: 'Error',
            detail: message,
          })
        })
      } else {
        toast.add({
          life: 5000,
          severity: 'error',
          summary: 'Error',
          detail: value,
        })
      }
    } else if (key === 'detail') {
      toast.add({
        life: 5000,
        severity: 'error',
        summary: 'Error',
        detail: value,
      })
    } else {
      if (Array.isArray(value)) {
        value.forEach((message) => {
          toast.add({
            life: 5000,
            severity: 'error',
            summary: 'Error',
            detail: message,
          })
        })
      } else {
        toast.add({
          life: 5000,
          severity: 'error',
          summary: 'Error',
          detail: value,
        })
      }
    }
  }
}
